import { useState, type FormEvent } from 'react'
import { useAuth } from '../lib/auth'
import { Spinner } from '../components/ui'
import { ThemeSwitcher } from '../components/ThemeSwitcher'
import { useTheme } from '../lib/theme'

/**
 * The sign-in screen, and the first thing a reseller's customer ever sees of
 * the product — so it is the most heavily branded surface in the app. The
 * background image, logo, product name and accent all come from
 * /branding/public, resolved from the Host header before authentication.
 */
export function Login() {
  const { signIn, branding } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const productName = branding?.productName ?? 'SmartVision IoT'

  // The logo carries black type, so on a dark surface the white-on-dark
  // variant is used. A reseller without a dark variant keeps its one logo.
  const { resolved } = useTheme()
  const logoSrc =
    resolved === 'dark'
      ? (branding?.logoDarkUrl ?? branding?.logoUrl ?? '/logo-dark.png')
      : (branding?.logoUrl ?? '/logo.png')

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await signIn(email.trim(), password)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed')
      setBusy(false)
    }
  }

  return (
    <div className="h-full grid lg:grid-cols-[1fr_minmax(420px,34%)] relative z-10">
      {/* ---- Left: the atmosphere panel. Hidden on small screens, where it
             would just push the form below the fold. ---- */}
      <aside className="hidden lg:block relative overflow-hidden rule-r">
        {branding?.loginBgUrl ? (
          <img
            src={branding.loginBgUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-45"
          />
        ) : (
          <DefaultBackdrop />
        )}

        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(105deg, color-mix(in srgb, var(--color-void) 88%, transparent) 20%, transparent 70%)',
          }}
        />

        <div className="relative h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-3">
            <img
              src={logoSrc}
              alt={productName}
              className="h-32 w-auto object-contain object-left"
            />
          </div>

          <div className="max-w-md stagger">
            <div className="label mb-3">Fleet telematics</div>
            <h1 className="text-[2.5rem] leading-[1.08] font-semibold tracking-tight">
              Every vehicle,
              <br />
              <span style={{ color: 'var(--accent-text)' }}>every second</span>,
              <br />
              on one screen.
            </h1>
            <p className="text-[var(--color-ink-3)] text-sm mt-4 leading-relaxed max-w-sm">
              Live tracking, route replay, geofence alerts and driver scoring — for one van or a
              hundred thousand.
            </p>
          </div>

          <dl className="flex gap-8">
            {[
              ['Ingest latency', '< 500 ms'],
              ['Uptime target', '99.95%'],
              ['Protocols', 'TCP · UDP · MQTT'],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="label text-[10px]">{k}</dt>
                <dd className="tnum text-[15px] mt-1" style={{ color: 'var(--accent-text)' }}>
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </aside>

      {/* ---- Right: the form ---- */}
      <main
        className="relative flex items-center justify-center p-6 sm:p-10 overflow-y-auto"
        style={{ background: 'var(--color-hull)' }}
      >
        <ThemeSwitcher compact className="absolute top-4 right-4" />
        <div className="w-full max-w-[340px] stagger">
          <div className="lg:hidden mb-8">
            <img
              src={logoSrc}
              alt={productName}
              className="h-24 w-auto object-contain object-left"
            />
          </div>

          <h2 className="text-xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-[13px] text-[var(--color-ink-3)] mt-1 mb-7">
            Use the credentials issued by your fleet administrator.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="label block mb-1.5">Email</span>
              <input
                className="field"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </label>

            <label className="block">
              <span className="label block mb-1.5">Password</span>
              <div className="relative">
                <input
                  className="field pr-10"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 w-9 grid place-items-center text-[var(--color-ink-3)] hover:text-[var(--color-ink)] transition-colors"
                  onClick={() => setShowPassword((v) => !v)}
                  onMouseDown={(e) => e.preventDefault()}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <EyeIcon open={!showPassword} />
                </button>
              </div>
            </label>

            {error && (
              <div
                className="flex items-start gap-2.5 px-3 py-2.5 rounded-[3px] text-[13px]"
                style={{
                  color: 'var(--text-critical)',
                  background: 'color-mix(in srgb, var(--color-critical) 9%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-critical) 28%, transparent)',
                }}
                role="alert"
              >
                <span className="dot mt-1.5" style={{ background: 'currentColor' }} />
                <span>{error}</span>
              </div>
            )}

            <button className="btn btn-primary w-full h-9" type="submit" disabled={busy}>
              {busy ? <Spinner /> : null}
              {busy ? 'Signing in' : 'Sign in'}
            </button>
          </form>

          {(branding?.supportEmail || branding?.supportPhone) && (
            <div className="mt-8 pt-5 rule-t">
              <div className="label mb-2">Need help?</div>
              {branding.supportEmail && (
                <a
                  className="block text-[13px] text-[var(--color-ink-2)] hover:text-[var(--accent)] transition-colors"
                  href={`mailto:${branding.supportEmail}`}
                >
                  {branding.supportEmail}
                </a>
              )}
              {branding.supportPhone && (
                <a
                  className="block text-[13px] text-[var(--color-ink-2)] hover:text-[var(--accent)] transition-colors tnum"
                  href={`tel:${branding.supportPhone}`}
                >
                  {branding.supportPhone}
                </a>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

/** The peek toggle's glyph: an eye, struck through while the password shows. */
function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.8" />
      {!open && <path d="M4 20 20 4" />}
    </svg>
  )
}

/** A generated backdrop: concentric range rings and a faint route trace, so
 *  an unbranded install still looks deliberate rather than empty. */
function DefaultBackdrop() {
  return (
    <svg
      className="absolute inset-0 w-full h-full"
      viewBox="0 0 800 800"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <radialGradient id="glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M40 0H0v40" fill="none" stroke="var(--color-rule)" strokeWidth="0.6" />
        </pattern>
      </defs>

      <rect width="800" height="800" fill="var(--color-hull)" />
      <rect width="800" height="800" fill="url(#grid)" opacity="0.5" />
      <circle cx="430" cy="370" r="300" fill="url(#glow)" />

      {[90, 170, 250, 330].map((r, i) => (
        <circle
          key={r}
          cx="430"
          cy="370"
          r={r}
          fill="none"
          stroke="var(--accent)"
          strokeOpacity={0.16 - i * 0.03}
          strokeWidth="1"
          strokeDasharray={i % 2 ? '4 8' : undefined}
        />
      ))}

      <path
        d="M120 620 C 220 560, 250 460, 350 430 S 500 420, 560 330 S 640 220, 720 190"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1.8"
        strokeOpacity="0.5"
        strokeDasharray="1200"
        strokeDashoffset="1200"
        style={{ animation: 'draw 4s var(--ease-glide) 0.4s forwards' }}
      />
      <style>{`@keyframes draw { to { stroke-dashoffset: 0; } }`}</style>

      {[
        [350, 430],
        [560, 330],
        [720, 190],
      ].map(([x, y]) => (
        <circle key={`${x}`} cx={x} cy={y} r="4" fill="var(--accent)" opacity="0.9" />
      ))}
    </svg>
  )
}
